// ARPG 필드 컨트롤러(레인 F, REBIRTH-MODES-SPEC 3.1): 마을① 필드 문 → 이끼 유적 필드(32m 청크 5×5).
// 스트리밍: 영웅이 선 청크 둘레 3×3만 활성. 활성 청크의 몹 무리만 살아 있고(고정 풀 MOB_CAPACITY, 새 배열 없음), 멀어진 무리는 조용히 거둔다.
// 몹 무리: 자리에 서 있다가 어그로 9m(MobController.aggroRadius) 안에 들어오면 쫓는다. 다 잡으면 45초 뒤 다시 난다. 몹 레벨 = 영웅 레벨 ±2.
// 필드 이벤트: 3~5분마다 엘리트 무리 또는 보물 고블린(맞으면 도망, 황금 고블린 이동 재사용). 균열 조각 드롭, 귀환석·출구·숨은 던전·균열 입구.
// 몹 종류·AI 상태·피격 5종·드롭·EXP는 기존 경로(MobController·Combat·LootController) 그대로 탄다 — 이 파일은 자리·수명·보상만 정한다.
// 프레임 루프(step) 안에서는 객체를 만들지 않는다(입장·층 이동 같은 장면 전환 때만 만든다).
import { MOB_CAPACITY, MOB_STATS, MOBS } from '../../shared/config';
import { REGIONS } from '../../shared/regions';
import { freePlan, type StagePlan } from '../../shared/content/stages';
import { HeroAct, MobKind, MobState, Scene } from '../../shared/types';
import { MobSpecial } from '../../shared/specialMonster';
import { inRect } from '../../shared/levels';
import { gridOfMap, R1_STAGE_MAPS } from '../../shared/stageMaps';
import { Rng } from '../../core/rng';
import { ACTIVE_MAX, ACTIVE_RADIUS, CHUNK_COUNT, FIELD, FIELD_GRID_SPEC as G, RIFT, fieldDamageMult, fieldHpMult, fieldXpMult } from '../../shared/field/constants';
import { killXp, levelBandXpMult, xpToNext } from '../../shared/growth';
import { FIELD_DOOR, chunkAt, fieldZone1, type FieldDef, type PackSpot } from '../../shared/field/layout';
import { FieldEv, FieldEventKind, RiftPhase, type FieldEventView, type FieldView, type RiftView } from '../../shared/field/view';
import { fieldUnlocked } from '../../shared/field/unlock';
import type { FieldNetHook } from './netHook';
import { FieldSave } from './save';
import { RiftController } from './rift';
import { GriftSave } from './griftSave';
import { griftCap, griftSelectable } from '../../shared/field/greaterRift';
import { dayIndex } from '../../shared/daily';
import { PLAYABLE_HEROES } from '../../shared/types';
import { fieldKillsPerLevel } from '../../shared/field/relevel';
import { RELEVEL_RULE, relevelRuleOf } from '../rebirth';
import type { SaveStorage } from '../save';
import type { GameSim } from '../game';

const TICK_HZ = 60;
/** 필드 무리에 쓰는 몹 수 상한(이벤트·균열 여유를 남긴다) */
const PACK_MOB_CAP = MOB_CAPACITY - 12;
/** 슬롯 주인 표시: -1 없음, -2 이벤트 몹, 0.. 무리 번호 */
const OWNER_NONE = -1, OWNER_EVENT = -2;
type Mutable<T> = { -readonly [K in keyof T]: T[K] };
export const FieldMode = { None: 0, Field: 1, Rift: 2, Hidden: 3 } as const;

class EventState implements FieldEventView {
  kind = 0; x = 0; z = 0; remainTicks = 0; nextTicks = 0; serial = 0; alive = 0; cleared = 0;
}
export class RiftState implements RiftView {
  phase = 0; gauge = 0; floor = 0; floors = 0; elapsedTicks = 0; limitTicks = RIFT.limitSec * TICK_HZ;
  portalActive = false; portalX = 0; portalZ = 0; chestActive = false; chestX = 0; chestZ = 0;
  rewardGold = 0; rewardStones = 0; clears = 0;
  // 대균열(C12): tier 0 = 일반 균열. choice = 입구 선택 띠가 열렸는지
  tier = 0; best = 0; cap = 0; selectable = 0; keys = 0; choice = false;
  /** 마지막 대균열 결과: 0 없음, 1 시간 안 클리어, 2 시간 초과 */
  result = 0; newBest = 0; prevBest = 0; milestoneStones = 0;
}
export class FieldState implements FieldView {
  unlocked = false; active = false; zoneName = ''; heroChunk = -1;
  readonly activeChunks = new Int8Array(ACTIVE_MAX).fill(-1);
  activeCount = 0; chunkSerial = 0; shards = 0; returnHold = 0; hiddenFound = false;
  readonly event = new EventState();
  readonly rift = new RiftState();
  mobLevel = 1;
  packState: Uint8Array = new Uint8Array(0);
  packCount = 0;
  net: FieldNetHook | null = null;
  mapOpen = false;
}

export class FieldController {
  readonly view = new FieldState();
  readonly def: FieldDef;
  readonly save: FieldSave;
  /** 대균열 저장(C12, dungeonhero.grift.v1) */
  readonly grift: GriftSave;
  readonly rift: RiftController;
  /** 필드 판정·스폰 전용 결정적 난수(game.rng를 흔들지 않는다) */
  readonly rng: Rng;
  /** 마지막으로 들어간 모드(재도전이 같은 곳으로 간다). 마을·스테이지로 가면 None */
  mode: number = FieldMode.None;
  /** 판 계획(필드 몹 레벨·배율을 영웅 레벨에 맞춰 고친다) */
  readonly plan: Mutable<StagePlan>;
  /** 무리 상태(view.packState와 같은 배열) */
  private readonly packState: Uint8Array;
  private readonly packChunk: Int16Array;
  private readonly packTimer: Int32Array;
  private readonly packAlive: Uint8Array;
  private readonly packLevel: Int16Array;
  private readonly packX: Float32Array;
  private readonly packZ: Float32Array;
  private readonly packDef: readonly PackSpot[];
  readonly slotOwner = new Int16Array(MOB_CAPACITY).fill(OWNER_NONE);
  private readonly slotGen = new Uint16Array(MOB_CAPACITY);
  private readonly chunkActive = new Uint8Array(CHUNK_COUNT);
  private packMobs = 0;
  private eventMobs = 0;
  private eventFlee = false;
  private eventGoblin = -1;
  private leashClock = 0;
  private wasInDoor = true;
  private wasInExit = true;
  private wasInRift = true;
  private wasInHidden = true;
  private holdTicks = 0;
  private lastLevel = -1;
  private unlockClock = 0;
  constructor(private readonly g: GameSim, seed: number, storage: SaveStorage | null) {
    this.def = fieldZone1();
    this.save = new FieldSave(storage);
    this.grift = new GriftSave(storage);
    this.rng = new Rng((seed * 2654435761 + 0x5f1e1d) >>> 0);
    let n = 0;
    for (const c of this.def.chunks) n += c.packs.length;
    this.packState = new Uint8Array(n); this.packChunk = new Int16Array(n); this.packTimer = new Int32Array(n);
    this.packAlive = new Uint8Array(n); this.packLevel = new Int16Array(n); this.packX = new Float32Array(n); this.packZ = new Float32Array(n);
    let k = 0;
    const defs: PackSpot[] = [];
    for (const c of this.def.chunks) for (const p of c.packs) { this.packChunk[k] = c.index; this.packX[k] = p.x; this.packZ[k] = p.z; defs.push(p); k++; }
    this.packDef = defs;
    this.view.packState = this.packState; this.view.packCount = n;
    this.view.zoneName = this.def.name;
    this.view.shards = this.save.shards;
    this.view.hiddenFound = this.save.data.hiddenFound;
    this.view.unlocked = fieldUnlocked(null);
    const base = freePlan('r1');
    this.plan = { ...base, map: this.def.map, kind: 'free', stage: null, xpDiff: 1, goldDiff: 1 };
    this.rift = new RiftController(g, this);
  }

  /** MMO 연결점(레인 M) */
  get net(): FieldNetHook | null { return this.view.net; }
  set net(n: FieldNetHook | null) { this.view.net = n; }
  get mapOpen(): boolean { return this.view.mapOpen; }
  get unlocked(): boolean { return this.view.unlocked; }
  /** 시험·통합용: 열림 판정을 다시 읽는다(레인 R 환생 상태가 바뀌었을 때) */
  refreshUnlock(view?: { readonly rebirth?: { readonly cycle: number } | null } | null): void { this.view.unlocked = fieldUnlocked(view ?? (this.g.view as unknown as { rebirth?: { cycle: number } })); }

  /** 영웅 레벨에 맞춘 몹 레벨·배율(필드·균열·숨은 던전 공통) */
  levelPlan(): void {
    const L = Math.max(1, this.g.growth.level);
    // 캐시 키 = 레벨 + 회차 + 재육성 규칙(같은 레벨이라도 영웅 전환·환생으로 표가 바뀐다, PR #328 리뷰 P3)
    const cur = this.g.rebirth.cur, rule = relevelRuleOf(cur), key = L * 1000 + cur.cycle * 10 + rule;
    if (key === this.lastLevel) return;
    this.lastLevel = key;
    this.view.mobLevel = L;
    this.plan.mobLevel = L;
    this.plan.mobHpMult = fieldHpMult(L);
    this.plan.mobDamageMult = fieldDamageMult(L);
    this.plan.envDamageMult = fieldDamageMult(L);
    // 필드 규칙('처치 N마리 = 1레벨')은 레벨 요구 EXP 표만 보고 맞춘다. grantXp(adjustedXp)가 고레벨 배율(C10-3)을 또 곱하므로
    // 여기서 나눠 상쇄한다 — 고레벨 배율은 스테이지 사냥에만 걸리고 필드 재육성 속도는 그대로(PR #317 리뷰 P1)
    // C12: 레벨당 처치 수는 1회차 그 레벨 시간 × 재육성 비율(1차 0.30·2차 0.25)에서 온다(shared/field/relevel.ts).
    // 이 배포 전에 환생한 영웅(rr 없음)은 그 회차 동안 옛 규칙(120마리 = 1레벨)을 지킨다(relevelRuleOf)
    this.plan.xpDiff = fieldXpMult(L, xpToNext(L), killXp(MobKind.Goblin, false, 'r1', 1), rule >= RELEVEL_RULE ? fieldKillsPerLevel(L, cur.cycle) : FIELD.killsPerLevel) / levelBandXpMult(L);
  }

  // ── 명령 ────────────────────────────────────────
  command(op: 'enter' | 'return' | 'map' | 'rift' | 'grift' | 'gclose', tier = 0): void {
    const f = this.g.flow;
    if (op === 'rift' || op === 'grift' || op === 'gclose') { this.riftChoice(op, tier); return; }
    if (op === 'enter') { if (f.scene === Scene.Village) this.enterField(null); return; }
    if (op === 'return') { if (f.scene === Scene.Field || f.scene === Scene.Rift) this.toVillage(); return; }
    if (op === 'map') this.view.mapOpen = f.scene === Scene.Field && !this.view.mapOpen && !f.paused;
  }

  /** 결과·패배 화면 「다시 도전」: 마지막 모드가 필드면 필드 입구로(균열은 잃고 필드로). 처리했으면 true */
  retry(): boolean {
    const f = this.g.flow;
    if (this.mode === FieldMode.None || (f.scene !== Scene.Defeat && f.scene !== Scene.Result)) return false;
    if (f.scene === Scene.Result && this.g.result.cleared && this.g.result.pickedCard < 0) return false;
    if (this.mode === FieldMode.Hidden) { this.enterHidden(); return true; }
    this.enterField(null);
    return true;
  }

  /** 필드 입장(마을① 필드 문·재도전·균열 출구). at = 시작 자리(없으면 남쪽 입구) */
  enterField(at: { x: number; z: number } | null): boolean {
    const g = this.g, f = g.flow;
    if (!this.view.unlocked) return false;
    if (f.scene === Scene.Village && (f.paused || f.travelTo > 0 || g.village.open || f.town !== 1)) return false;
    this.levelPlan();
    g.flowController.loadMap('r1', null, {
      scene: Scene.Field, plan: this.plan, map: this.def.map, grid: this.def.grid,
      label: this.def.name + ' · 구역 1', objective: '필드를 돌며 사냥하세요. 균열 조각 5개로 균열이 열린다.',
    });
    if ((f.scene as number) !== Scene.Field) return false;
    this.mode = FieldMode.Field;
    g.mobController.aggroRadius = FIELD.aggroRadius;
    if (at) { g.snapHero(at.x, at.z); g.hero.facing = 0; }
    this.resetField();
    return true;
  }

  private resetField(): void {
    const v = this.view;
    this.packState.fill(0); this.packTimer.fill(0); this.packAlive.fill(0);
    for (let k = 0; k < this.packState.length; k++) this.packLevel[k] = 0;
    this.slotOwner.fill(OWNER_NONE);
    this.packMobs = 0; this.eventMobs = 0; this.eventGoblin = -1; this.eventFlee = false;
    const e = v.event;
    e.kind = FieldEventKind.None; e.remainTicks = 0; e.alive = 0;
    e.nextTicks = Math.round((FIELD.firstEventSec[0] + this.rng.next() * (FIELD.firstEventSec[1] - FIELD.firstEventSec[0])) * TICK_HZ);
    v.heroChunk = -1; v.returnHold = 0; v.mapOpen = false;
    this.holdTicks = 0;
    // 입장 틱에 서 있는 칸은 이미 안에 있던 것으로 본다(입구 위에서 시작해도 바로 나가지 않게)
    this.wasInExit = true; this.wasInRift = true; this.wasInHidden = true;
    this.updateChunks();
  }

  toVillage(): void {
    this.mode = FieldMode.None;
    this.view.mapOpen = false;
    this.g.flowController.loadMap('village');
    this.wasInDoor = true;
  }

  // ── 고정 스텝 ────────────────────────────────────
  step(): void {
    const g = this.g, sc = g.flow.scene;
    const v = this.view;
    v.active = sc === Scene.Field || sc === Scene.Rift;
    if (v.shards !== this.save.shards) v.shards = this.save.shards;
    if (sc === Scene.Village) { this.villageDoor(); return; }
    if (sc !== Scene.Field && sc !== Scene.Rift) { if (sc === Scene.Dungeon && this.mode !== FieldMode.Hidden) this.mode = FieldMode.None; return; }
    // MMO 연결점: 필드 장면 틱마다 내 영웅을 넘긴다(레인 M — 없으면 null)
    this.view.net?.update(g.hero);
    this.levelPlan();
    if (sc === Scene.Rift) { this.trackKills(); this.rift.step(); return; }
    this.updateChunks();
    this.trackKills();
    this.stepPacks();
    this.stepEvent();
    this.triggers();
  }

  /** 마을①: 필드 문에 걸어 들어가면 필드로(열린 계정만) */
  private villageDoor(): void {
    const g = this.g, f = g.flow, h = g.hero;
    if (this.mode !== FieldMode.None && this.mode !== FieldMode.Hidden) this.mode = FieldMode.None;
    // 열림 판정을 0.5초마다 다시 읽는다(레인 R 환생 상태 view.rebirth가 생기거나 바뀌면 문이 따라 열린다 — fieldUnlocked 하나만 본다)
    if (g.worldTick && ++this.unlockClock >= 30) { this.unlockClock = 0; this.refreshUnlock(); }
    if (!this.view.unlocked || f.town !== 1 || f.travelTo > 0 || g.village.open) { this.wasInDoor = true; return; }
    const inside = inRect(FIELD_DOOR, h.x, h.z);
    if (inside && !this.wasInDoor) this.enterField(null);
    this.wasInDoor = inside;
  }

  /** 영웅 청크 둘레 3×3(구역 가장자리에서는 잘린다)을 활성으로. 바뀔 때만 다시 센다 */
  private updateChunks(): void {
    const v = this.view, h = this.g.hero;
    const c = chunkAt(h.x, h.z);
    if (c === v.heroChunk) return;
    v.heroChunk = c;
    this.chunkActive.fill(0);
    let n = 0;
    if (c >= 0) {
      const cx = c % G.cols, cz = (c / G.cols) | 0;
      for (let dz = -ACTIVE_RADIUS; dz <= ACTIVE_RADIUS; dz++) for (let dx = -ACTIVE_RADIUS; dx <= ACTIVE_RADIUS; dx++) {
        const x = cx + dx, z = cz + dz;
        if (x < 0 || z < 0 || x >= G.cols || z >= G.rows) continue;
        const i = z * G.cols + x;
        this.chunkActive[i] = 1; v.activeChunks[n++] = i;
      }
    }
    for (let i = n; i < v.activeChunks.length; i++) v.activeChunks[i] = -1;
    v.activeCount = n; v.chunkSerial++;
    const e = this.g.emit(FieldEv.Chunks); e.a = c; e.b = n;
  }
  chunkIsActive(i: number): boolean { return this.chunkActive[i] === 1; }

  // ── 몹 슬롯 추적(처치·소멸) ───────────────────────
  /** 몹 하나를 낸다(필드 레벨 체력). 실패하면 -1 */
  spawnMob(kind: number, elite: boolean, x: number, z: number, level: number, owner: number, hunting = false, hpMult = 1): number {
    const g = this.g;
    const slot = g.flowController.spawnNear(kind, elite, x, z, hunting ? 0 : -1);
    if (slot < 0) return -1;
    const m = g.mobs;
    m.hp[slot] = m.maxHp[slot] = Math.max(1, Math.round(MOB_STATS[kind].hp * fieldHpMult(level) * hpMult * (m.elite[slot] ? MOBS.elite.hpMult : 1)));
    this.slotOwner[slot] = owner; this.slotGen[slot] = m.gen[slot];
    return slot;
  }
  /** 주인 있는 슬롯을 놓는다(처치 집계 없이) */
  private release(slot: number): void {
    const o = this.slotOwner[slot];
    if (o === OWNER_NONE) return;
    this.slotOwner[slot] = OWNER_NONE;
    if (o === OWNER_EVENT) this.eventMobs--;
    else if (o >= 0) { this.packAlive[o]--; this.packMobs--; }
  }
  /** 슬롯을 조용히 거둔다(보상 없음) */
  vanish(slot: number): void {
    this.release(slot);
    this.g.mobController.vanish(slot);
  }
  /** 매 틱: 주인 있는 슬롯이 쓰러졌으면(hp 0 = 처치) 보상·집계, 사라졌으면 집계만 */
  private trackKills(): void {
    const m = this.g.mobs;
    for (let i = 0; i < MOB_CAPACITY; i++) {
      const o = this.slotOwner[i];
      if (o === OWNER_NONE) continue;
      const st = m.state[i];
      if (m.gen[i] !== this.slotGen[i]) { this.release(i); continue; }
      if (st !== MobState.Dying && st !== MobState.Inactive) continue;
      const killed = m.hp[i] <= 0;
      const elite = m.elite[i] !== 0;
      this.release(i);
      if (!killed) continue;
      this.onKill(i, o, elite);
    }
  }
  private onKill(slot: number, owner: number, elite: boolean): void {
    const g = this.g, m = g.mobs;
    if (g.flow.scene === Scene.Rift) { this.rift.onKill(elite); return; }
    // 균열 조각: 일반 3%, 엘리트 25%
    if (this.rng.next() < (elite ? FIELD.shardChance.elite : FIELD.shardChance.mob)) this.giveShards(1, m.x[slot], m.z[slot]);
    if (owner >= 0 && this.packAlive[owner] === 0 && this.packState[owner] === 1) {
      this.packState[owner] = 2; this.packTimer[owner] = FIELD.respawnSec * TICK_HZ;
    }
    if (owner === OWNER_EVENT && slot === this.eventGoblin) this.eventGoblin = -2; // 처치됨
  }
  giveShards(n: number, x: number, z: number): void {
    const now = this.save.addShards(n);
    this.view.shards = now;
    const e = this.g.emit(FieldEv.Shard); e.x = x; e.z = z; e.a = now; e.b = n;
  }

  // ── 몹 무리 ──────────────────────────────────────
  private stepPacks(): void {
    const g = this.g, h = g.hero, m = g.mobs, whole = g.worldTick;
    const L = this.view.mobLevel;
    const leash = whole && ++this.leashClock % 30 === 0;
    for (let k = 0; k < this.packState.length; k++) {
      const st = this.packState[k], chunk = this.packChunk[k];
      if (st === 2) { if (whole && --this.packTimer[k] <= 0) this.packState[k] = 0; continue; }
      const px = this.packX[k], pz = this.packZ[k];
      if (st === 0) {
        if (!this.chunkActive[chunk] || Math.hypot(h.x - px, h.z - pz) < FIELD.spawnClearance) continue;
        const spot = this.packDef[k];
        if (this.packMobs + spot.size > PACK_MOB_CAP) continue;
        const level = Math.max(1, L + spot.levelOffset);
        this.packLevel[k] = level;
        let n = 0;
        for (let j = 0; j < spot.size; j++) {
          const a = (j / spot.size) * Math.PI * 2, r = j === 0 ? 0 : 1.7;
          const s = this.spawnMob(spot.kinds[j], false, px + Math.sin(a) * r, pz + Math.cos(a) * r, level, k);
          if (s >= 0) { n++; this.packMobs++; }
        }
        this.packAlive[k] = n;
        if (n > 0) { this.packState[k] = 1; const e = g.emit(FieldEv.PackSpawn); e.a = k; e.b = n; e.x = px; e.z = pz; }
        continue;
      }
      // 살아 있는 무리: 청크가 비활성이고 모두 대기·멀면 거둔다(다시 활성이 되면 처음부터 낸다)
      if (!this.chunkActive[chunk]) {
        let calm = true;
        for (let i = 0; i < MOB_CAPACITY && calm; i++) {
          if (this.slotOwner[i] !== k) continue;
          if (m.state[i] !== MobState.Idle && m.state[i] !== MobState.Spawning) calm = false;
          else if (Math.hypot(m.x[i] - h.x, m.z[i] - h.z) < FIELD.dormantDist) calm = false;
        }
        if (calm) {
          for (let i = 0; i < MOB_CAPACITY; i++) if (this.slotOwner[i] === k) this.vanish(i);
          this.packState[k] = 0;
        }
        continue;
      }
      // 대기 몹이 자리에서 멀어지면 자리 쪽으로 돌아선다(배회 방향만 바꾼다 — 이동은 MobController 그대로)
      if (leash) for (let i = 0; i < MOB_CAPACITY; i++) {
        if (this.slotOwner[i] !== k || m.state[i] !== MobState.Idle) continue;
        const dx = px - m.x[i], dz = pz - m.z[i];
        if (dx * dx + dz * dz > FIELD.homeLeash * FIELD.homeLeash) m.facing[i] = Math.atan2(dx, dz);
      }
    }
  }
  // ── 필드 이벤트 ──────────────────────────────────
  private stepEvent(): void {
    const g = this.g, e = this.view.event, whole = g.worldTick, m = g.mobs;
    if (!whole) return;
    e.alive = this.eventMobs;
    if (e.kind === FieldEventKind.None) {
      if (--e.nextTicks <= 0) this.startEvent();
      return;
    }
    // 보물 고블린: 맞으면 도망 시간으로 바꾼다
    if (e.kind === FieldEventKind.Goblin && this.eventGoblin >= 0 && !this.eventFlee && m.hp[this.eventGoblin] < m.maxHp[this.eventGoblin]) {
      this.eventFlee = true; e.remainTicks = Math.min(e.remainTicks, FIELD.goblin.fleeSec * TICK_HZ);
    }
    if (e.kind === FieldEventKind.Goblin && this.eventGoblin >= 0) { e.x = m.x[this.eventGoblin]; e.z = m.z[this.eventGoblin]; }
    if (this.eventMobs === 0) { this.endEvent(this.eventGoblin === -2 || e.kind === FieldEventKind.Elite); return; }
    if (--e.remainTicks <= 0) {
      for (let i = 0; i < MOB_CAPACITY; i++) if (this.slotOwner[i] === OWNER_EVENT) this.vanish(i);
      this.endEvent(false);
    }
  }

  private startEvent(): void {
    const g = this.g, h = g.hero, e = this.view.event;
    // 영웅에게서 18~56m 떨어진 청크 공터(입구 청크 제외) 중 하나
    let pick = -1, seen = 0;
    for (const c of this.def.chunks) {
      if (!c.event) continue;
      const d = Math.hypot(c.event.x - h.x, c.event.z - h.z);
      if (d < FIELD.eventDist[0] || d > FIELD.eventDist[1]) continue;
      seen++;
      if (this.rng.next() * seen < 1) pick = c.index;
    }
    if (pick < 0) { e.nextTicks = 10 * TICK_HZ; return; }
    const at = this.def.chunks[pick].event!;
    const elite = this.rng.next() < FIELD.eliteEventChance;
    const L = this.view.mobLevel;
    let n = 0;
    this.eventGoblin = -1; this.eventFlee = false;
    if (elite) {
      const s = FIELD.eliteEvent, kinds = [MobKind.ShieldGoblin, MobKind.Goblin, MobKind.Boar, MobKind.Goblin, MobKind.Goblin];
      for (let j = 0; j < s.size; j++) {
        const a = (j / s.size) * Math.PI * 2, r = j === 0 ? 0 : 1.9;
        if (this.spawnMob(kinds[j % kinds.length], j < s.elites, at.x + Math.sin(a) * r, at.z + Math.cos(a) * r, L + s.levelAdd, OWNER_EVENT) >= 0) { n++; this.eventMobs++; }
      }
    } else {
      const s = this.spawnMob(MobKind.Goblin, false, at.x, at.z, L + 1, OWNER_EVENT);
      if (s >= 0) {
        n = 1; this.eventMobs++; this.eventGoblin = s;
        // 황금 고블린 이동(공격하지 않고, 맞으면 영웅 반대쪽으로 도망)을 그대로 쓴다
        g.mobs.special[s] = MobSpecial.GoldGoblin;
        g.special.state.fleeing = false;
        g.mobs.hp[s] = g.mobs.maxHp[s] = Math.round(g.mobs.maxHp[s] * 3);
      }
    }
    if (n === 0) { e.nextTicks = 10 * TICK_HZ; return; }
    e.kind = elite ? FieldEventKind.Elite : FieldEventKind.Goblin;
    e.x = at.x; e.z = at.z; e.serial++;
    e.remainTicks = (elite ? FIELD.eventLifeSec : FIELD.goblin.idleSec) * TICK_HZ;
    e.alive = n;
    const ev = g.emit(FieldEv.EventStart); ev.x = at.x; ev.z = at.z; ev.a = e.kind; ev.b = Math.round(e.remainTicks / TICK_HZ);
  }

  private endEvent(cleared: boolean): void {
    const g = this.g, e = this.view.event;
    let shards = 0, gold = 0;
    if (cleared) {
      shards = e.kind === FieldEventKind.Elite ? FIELD.shardReward.elite : FIELD.shardReward.goblin;
      gold = Math.round(REGIONS.r1.reward.base * (e.kind === FieldEventKind.Goblin ? FIELD.goblin.goldMult : FIELD.eventGoldMult) * fieldHpMult(this.view.mobLevel) * g.stats.goldMult);
      if (gold > 0) g.save.addGold(gold);
      this.giveShards(shards, e.x, e.z);
      e.cleared++;
    }
    const ev = g.emit(FieldEv.EventEnd); ev.x = e.x; ev.z = e.z; ev.a = e.kind; ev.b = cleared ? 1 : 0; ev.c = shards; ev.d = gold;
    e.kind = FieldEventKind.None; e.alive = 0; e.remainTicks = 0;
    this.eventGoblin = -1; this.eventFlee = false;
    e.nextTicks = Math.round((FIELD.eventEverySec[0] + this.rng.next() * (FIELD.eventEverySec[1] - FIELD.eventEverySec[0])) * TICK_HZ);
  }

  // ── 자리(출구·귀환석·균열·숨은 던전) ──────────────
  private triggers(): void {
    const g = this.g, h = g.hero, d = this.def, v = this.view, f = g.flow;
    if (h.hp <= 0 || h.act === HeroAct.Dead) return;
    // 남쪽 출구: 걸어 나가면 마을로
    const inExit = inRect(d.exit, h.x, h.z);
    if (inExit && !this.wasInExit) { this.toVillage(); return; }
    this.wasInExit = inExit;
    // 귀환석: 곁에 가만히 1.5초 서 있으면 마을로
    const rs = d.returnStone;
    // 싸우는 도중 지나가다 돌아가지 않게, 가만히 선(Idle) 채로만 센다
    if (Math.hypot(h.x - rs.x, h.z - rs.z) <= rs.r + 0.4 && h.act === HeroAct.Idle) {
      this.holdTicks++;
      v.returnHold = Math.min(1, this.holdTicks / (FIELD.returnHoldSec * TICK_HZ));
      if (this.holdTicks >= FIELD.returnHoldSec * TICK_HZ) { this.toVillage(); return; }
    } else { this.holdTicks = 0; v.returnHold = 0; }
    // 균열 입구: 조각 5개 이상이면 들어가며 5개를 쓴다
    const rp = d.riftPortal;
    const inRift = Math.hypot(h.x - rp.x, h.z - rp.z) <= rp.r;
    if (inRift && !this.wasInRift) {
      // 1차 환생 뒤에는 입구에서 일반 균열·대균열을 고른다(아래 가장자리 선택 띠 — 판 위를 가리지 않음)
      if (this.refreshGrift() > 0) { v.rift.choice = true; this.wasInRift = inRift; return; }
      if (this.save.shards >= RIFT.shards) {
        this.save.spendShards(RIFT.shards); v.shards = this.save.shards;
        this.rift.enter();
        return;
      }
      f.objectiveLabel = `균열이 닫혀 있다: 균열 조각 ${this.save.shards}/${RIFT.shards}`;
    }
    // 선택 띠가 열려 있는 동안 KST 날짜가 바뀌면(자정 넘김) 열쇠·도전 가능 여부를 새로 채운다(정수 비교뿐, 할당 없음)
    if (v.rift.choice && dayIndex(g.wallClock()) !== this.griftDay) this.refreshGrift();
    // 선택 띠는 입구에서 조금 벗어나면 닫힌다
    if (v.rift.choice && Math.hypot(h.x - rp.x, h.z - rp.z) > rp.r + 2.5) v.rift.choice = false;
    this.wasInRift = inRift;
    // 숨은 던전: 12m 안에 오면 찾은 것으로(미니맵 표시), 들어서면 기존 스테이지 지도 한 장으로
    const hd = d.hidden;
    const dh = Math.hypot(h.x - hd.x, h.z - hd.z);
    if (!v.hiddenFound && dh < 12) { v.hiddenFound = true; this.save.markHidden(); const e = g.emit(FieldEv.HiddenFound); e.x = hd.x; e.z = hd.z; }
    const inHidden = dh <= hd.r;
    if (inHidden && !this.wasInHidden) { this.enterHidden(); return; }
    this.wasInHidden = inHidden;
  }

  /** 숨은 던전: 지역1 수로 광장(B) 지도를 자유 판으로(진행 기록 없음), 몹 레벨은 영웅 레벨 */
  enterHidden(): void {
    const g = this.g;
    this.levelPlan();
    const map = R1_STAGE_MAPS.b;
    const base = freePlan('r1');
    const plan: StagePlan = {
      ...base, map, rooms: map.rooms.map((r) => ({ kind: r.kind, waves: r.waves, boss: r.kind === 'boss' ? (r.boss ?? 'treant') : undefined })),
      mobLevel: this.plan.mobLevel, mobHpMult: this.plan.mobHpMult, mobDamageMult: this.plan.mobDamageMult, envDamageMult: this.plan.envDamageMult, xpDiff: this.plan.xpDiff,
    };
    g.flowController.loadMap('r1', null, { scene: Scene.Dungeon, plan, map, grid: gridOfMap(map), label: '숨은 던전 · ' + map.name, objective: '숨은 던전: 첫 전투방으로 이동하세요.' });
    if (g.flow.scene === Scene.Dungeon) this.mode = FieldMode.Hidden;
  }

  // ── 대균열(C12) ─────────────────────────────────
  /** 이 캐릭터의 대균열 상한 입력: 회차(영웅 중 최고) · 지금 영웅 레벨 · 다른 영웅 레벨 합 · 숙련 */
  griftInputs(): { cycle: number; heroLevel: number; altLevels: number; mastery: number } {
    const g = this.g;
    let cycle = 0, alt = 0;
    for (const id of PLAYABLE_HEROES) {
      cycle = Math.max(cycle, g.rebirth.cycleOf(id));
      if (id !== g.heroId) alt += g.growthByHero[id]?.level ?? 0;
    }
    return { cycle, heroLevel: g.growth.level, altLevels: alt, mastery: g.mastery?.level ?? 0 };
  }
  /** 열쇠·상한·최고 기록을 뷰에 다시 채운다. 반환 = 상한(0이면 잠김) */
  /** 마지막으로 열쇠를 갱신한 KST 날짜 번호(자정 넘김 감지용) */
  private griftDay = -1;
  refreshGrift(): number {
    const r = this.view.rift, i = this.griftInputs();
    r.cap = griftCap(i.cycle, i.heroLevel, i.altLevels, i.mastery);
    this.grift.pull();  // 다른 탭이 올린 최고 기록·열쇠를 먼저 받아 온다(PR #343 리뷰 P1)
    r.best = this.grift.data.best;
    r.selectable = griftSelectable(r.best, r.cap);
    this.griftDay = dayIndex(this.g.wallClock());
    r.keys = r.cap > 0 ? this.grift.refreshKeys(this.griftDay) : this.grift.data.keys;
    return r.cap;
  }
  /** 입구 선택 띠 명령: 일반 균열(조각 5) · 대균열 n단계(열쇠 1) · 닫기 */
  private riftChoice(op: 'rift' | 'grift' | 'gclose', tier: number): void {
    const g = this.g, f = g.flow, v = this.view;
    if (f.scene !== Scene.Field || !v.rift.choice) return;
    if (op === 'gclose') { v.rift.choice = false; return; }
    if (op === 'rift') {
      if (this.save.shards < RIFT.shards) { f.objectiveLabel = `균열 조각이 모자라다: ${this.save.shards}/${RIFT.shards}`; return; }
      this.save.spendShards(RIFT.shards); v.shards = this.save.shards; v.rift.choice = false;
      this.rift.enter();
      return;
    }
    const cap = this.refreshGrift();
    const t = Math.floor(tier);
    if (cap <= 0 || !(t >= 1 && t <= v.rift.selectable)) return;
    if (!this.grift.spendKey()) { f.objectiveLabel = '대균열 열쇠가 없다: 하루 2개(자정에 채워짐, 최대 6개)'; return; }
    v.rift.keys = this.grift.data.keys; v.rift.choice = false;
    this.rift.enterGreater(t, `gr${this.grift.data.tries}t${t}`);
  }

  /** 고정 스텝 밖 저장(GameSim.flushSaves) */
  flush(): void { this.save.flush(); this.grift.flush(); }
}
